import React, { useState } from 'react';
import { X, Bot, BarChart3, Copy, Briefcase, ArrowRight, CheckCircle } from 'lucide-react';

const ProductsPage = ({ isOpen, onClose }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const products = [
    {
      id: 1,
      icon: Bot,
      iconColor: 'text-cyan-400',
      iconBg: 'from-cyan-500/20 to-cyan-600/20',
      badge: 'Marketwise Flagship',
      badgeColor: 'bg-gradient-to-r from-cyan-500 to-blue-600',
      title: 'Marketwise AI Softwares',
      description: 'The flagship Marketwise AI that has generated 847% returns. Never sleeps, never loses focus, always profitable.',
      features: [
        'Marketwise trades XAUUSD, EURUSD, BTCUSD',
        'Works on MT4 & MT5 platforms',
        'Marketwise AI processes 10K+ signals/second',
        'Advanced Marketwise risk management',
        '2+ years verified Marketwise performance'
      ]
    },
    {
      id: 2,
      icon: BarChart3,
      iconColor: 'text-blue-400',
      iconBg: 'from-blue-500/20 to-blue-600/20',
      badge: 'Marketwise Powered',
      badgeColor: 'bg-gradient-to-r from-blue-500 to-indigo-600',
      title: 'Marketwise Signal Indicators',
      description: 'Professional-grade indicators powered by Marketwise AI algorithms for precise market entry and exit signals.',
      features: [
        'Marketwise indicators for MT4/MT5 & TradingView',
        'Marketwise AI signal generation',
        'Multi-timeframe Marketwise analysis',
        'Real-time Marketwise alerts',
        '94.7% Marketwise accuracy rate'
      ]
    },
    {
      id: 3,
      icon: Copy,
      iconColor: 'text-purple-400',
      iconBg: 'from-purple-500/20 to-purple-600/20',
      badge: 'Marketwise Challenge',
      badgeColor: 'bg-gradient-to-r from-purple-500 to-fuchsia-600',
      title: 'Marketwise Master Copy',
      description: 'Copy the exact trades from our most successful Marketwise AI systems with verified track records.',
      features: [
        'Copy verified Marketwise master accounts',
        'Marketwise verified track records',
        'Customizable Marketwise risk levels',
        'Real-time Marketwise synchronization',
        'Advanced Marketwise analytics'
      ]
    },
    {
      id: 4,
      icon: Briefcase,
      iconColor: 'text-orange-400',
      iconBg: 'from-orange-500/20 to-orange-600/20',
      badge: 'Marketwise Masters',
      badgeColor: 'bg-gradient-to-r from-orange-500 to-amber-600',
      title: 'Marketwise White Label',
      description: 'Launch Your Own AI Trading Brand using our technology—powered by Marketwise AI and branded as your own company. Ideal for entrepreneurs.',
      features: [
        'Custom Website & Branding – Your logo, colors, and domain',
        'Client Dashboard – Manage users, subscriptions, and performance',
        'Software Licensing & Support Panel',
        'Automated AI Trading Bots – Pre-configured and trading 24/7',
        'Custom Strategies – Tailored for your clients',
        'Scalable Infrastructure – Handle hundreds to thousands of clients'
      ]
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="fixed top-6 right-6 z-50 text-slate-400 hover:text-white transition-colors bg-slate-800/90 rounded-full p-3 hover:bg-slate-700"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Header Section */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Badge */}
        <div className="text-center mb-8">
          <span className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-semibold">
            ✦ Our Products
          </span>
        </div>

        {/* Main Heading */}
        <h1 className="text-5xl md:text-6xl font-bold text-center mb-6">
          Complete <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">AI Trading Solutions</span>
        </h1>

        {/* Description */}
        <p className="text-xl text-slate-400 text-center max-w-4xl mx-auto mb-12">
          Discover our comprehensive suite of AI-powered trading tools designed to maximize your profits across forex, crypto, and commodities markets.
        </p>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto mb-16">
          <div className="text-center">
            <div className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2">4</div>
            <div className="text-slate-400">Product Categories</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2">10K+</div>
            <div className="text-slate-400">Active Users</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mb-2">24/7</div>
            <div className="text-slate-400">Support</div>
          </div>
        </div>

        {/* Best Product Shortcut Button */}
        <div className="text-center mb-20">
          <button className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 font-semibold hover:bg-cyan-500/20 transition-all duration-300">
            <span>✦ Best Product Shortcut</span>
          </button>
        </div>

        {/* Trading Arsenal Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Complete <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Marketwise Trading Arsenal</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            The complete Marketwise ecosystem - from our flagship AI systems to specialized tools for AI Software and Single Indicator. Everything powered by Marketwise intelligence.
          </p>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto mb-20">
          {products.map((product) => {
            const IconComponent = product.icon;
            return (
              <div
                key={product.id}
                className="bg-gradient-to-br from-slate-900/80 to-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300"
              >
                {/* Icon and Badge */}
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${product.iconBg} rounded-xl flex items-center justify-center border border-slate-700/50`}>
                    <IconComponent className={`w-8 h-8 ${product.iconColor}`} />
                  </div>
                  <span className={`${product.badgeColor} text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg`}>
                    {product.badge}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-4">{product.title}</h3>

                {/* Description */}
                <p className="text-slate-400 mb-6 leading-relaxed">{product.description}</p>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    onClick={() => {
                      setSelectedProduct(product.title);
                      onClose();
                      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/30"
                  >
                    <span>Get Demo</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button className="px-6 py-3 bg-slate-800/50 hover:bg-slate-800 text-white font-semibold rounded-lg border border-slate-700 hover:border-cyan-500/50 transition-all duration-300">
                    Explore More
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500/50 rounded-2xl p-12 text-center">
            <h3 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your Marketwise AI Journey?
            </h3>
            <p className="text-slate-300 text-lg mb-8">
              Get personalized Marketwise recommendations based on your capital and goals with the perfect AI solution for your trading goals.
            </p>
            <button 
              onClick={() => {
                onClose();
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold rounded-lg text-lg transition-all duration-300 shadow-lg shadow-cyan-500/50"
            >
              <span>Start Marketwise AI Now</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
